import React from 'react';
import { SystemService } from 'services';
import moment from 'moment';

// ============================================================
// Shapes reales del backend (según spec C2).
// Las interfaces de SystemService son más laxas que la
// respuesta real; acá tipamos lo que realmente consumimos.
// ============================================================

interface HealthCheck {
	status?: 'ok' | 'warning' | 'critical' | 'error' | string;
	latency_ms?: number;
	error_message?: string | null;
	used_mb?: number;
	total_mb?: number;
	percent_used?: number;
	swap_used_mb?: number;
	swap_total_mb?: number;
	used_gb?: number;
	total_gb?: number;
	mount?: string;
	load_1m?: number;
	load_5m?: number;
	load_15m?: number;
	cpu_count?: number;
	system_seconds?: number;
	process_seconds?: number;
	started_at?: string;
	pm2_restarts?: number | null;
}

interface HealthFull {
	overall_status?: 'ok' | 'warning' | 'critical' | string;
	checks?: {
		database?: HealthCheck;
		memory?: HealthCheck;
		disk?: HealthCheck;
		cpu?: HealthCheck;
		uptime?: HealthCheck;
		node_version?: string;
		platform?: string;
	};
	checked_at?: string;
}

interface CronJob {
	job_name?: string;
	enabled?: boolean;
	schedule_description?: string;
	last_run?: string | null;
	last_status?: string | null;
	is_running_now?: boolean;
	stats_24h?: {
		runs?: number;
		successful?: number;
		failed?: number;
	};
}

interface CronStatusFull {
	jobs?: CronJob[];
	global_stats?: {
		total_runs_24h?: number;
		successful_24h?: number;
		failed_24h?: number;
		active_jobs?: number;
		disabled_jobs?: number;
	};
	server_time?: string;
}

interface BackupRow {
	filename?: string;
	size_bytes?: number;
	size_mb?: number;
	type?: string;
	created_at?: string;
	description?: string;
}

interface BackupsFull {
	backups?: BackupRow[];
	total_count?: number;
	total_size_mb?: number;
	backups_path?: string;
}

interface DashboardState {
	health: HealthFull | null;
	crons: CronStatusFull | null;
	backups: BackupsFull | null;
	healthError: boolean;
	cronsError: boolean;
	backupsError: boolean;
	loading: boolean;
	lastUpdated: Date | null;
	now: number;
}

const REFRESH_INTERVAL_MS = 30 * 1000;
const TICK_INTERVAL_MS = 1000;

// Formatea segundos como "Xd Yh", "Xh Ym Zs", etc.
const formatUptime = (seconds: number | undefined): string => {
	const s = Number(seconds) || 0;
	if (s <= 0) return '—';
	const days = Math.floor(s / 86400);
	const hours = Math.floor((s % 86400) / 3600);
	const mins = Math.floor((s % 3600) / 60);
	const secs = Math.floor(s % 60);
	if (days > 0) return `${ days }d ${ hours }h`;
	if (hours > 0) return `${ hours }h ${ mins }m ${ secs }s`;
	if (mins > 0) return `${ mins }m ${ secs }s`;
	return `${ secs }s`;
}

// Humaniza "hace X" a partir de un timestamp ISO
const humanizeAgo = (iso?: string | null): string => {
	if (!iso) return '—';
	const t = moment(iso);
	if (!t.isValid()) return '—';
	const diff = moment().diff(t, 'seconds');
	if (diff < 0) return 'ahora';
	if (diff < 5) return 'hace instantes';
	if (diff < 60) return `hace ${ diff } seg`;
	const mins = Math.floor(diff / 60);
	if (mins < 60) return `hace ${ mins } min`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `hace ${ hours }h`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `hace ${ days }d`;
	const months = Math.floor(days / 30);
	if (months < 12) return `hace ${ months } mes${ months === 1 ? '' : 'es' }`;
	const years = Math.floor(months / 12);
	return `hace ${ years } año${ years === 1 ? '' : 's' }`;
}

// "ok" -> verde, "warning" -> amarillo, "critical"/"error" -> rojo, otro -> gris
const statusClass = (status?: string): 'ok' | 'warning' | 'critical' | 'unknown' => {
	if (!status) return 'unknown';
	const s = String(status).toLowerCase();
	if (s === 'ok' || s === 'success') return 'ok';
	if (s === 'warning' || s === 'warn' || s === 'degraded') return 'warning';
	if (s === 'critical' || s === 'error' || s === 'down' || s === 'failed') return 'critical';
	return 'unknown';
}

const formatLatency = (ms?: number): string => {
	if (ms == null || isNaN(Number(ms))) return '—';
	return `${ Number(ms).toFixed(0) } ms`;
}

const formatNumber = (v: any): string => {
	const n = Number(v);
	if (isNaN(n)) return '0';
	return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

class Dashboard extends React.Component<{}, DashboardState> {

	refreshTimer: any = null;
	tickTimer: any = null;
	isMounted_: boolean = false;

	state: DashboardState = {
		health: null,
		crons: null,
		backups: null,
		healthError: false,
		cronsError: false,
		backupsError: false,
		loading: true,
		lastUpdated: null,
		now: Date.now(),
	}

	componentDidMount() {
		this.isMounted_ = true;
		this.loadAll(true);

		this.refreshTimer = setInterval(() => {
			this.loadAll(false);
		}, REFRESH_INTERVAL_MS);

		this.tickTimer = setInterval(() => {
			if (this.isMounted_) {
				this.setState({ now: Date.now() });
			}
		}, TICK_INTERVAL_MS);
	}

	componentWillUnmount() {
		this.isMounted_ = false;
		if (this.refreshTimer) {
			clearInterval(this.refreshTimer);
			this.refreshTimer = null;
		}
		if (this.tickTimer) {
			clearInterval(this.tickTimer);
			this.tickTimer = null;
		}
	}

	loadAll = async (isFirst: boolean) => {
		if (isFirst) {
			this.setState({ loading: true });
		}

		const [healthRes, cronsRes, backupsRes] = await Promise.allSettled([
			SystemService.admin.getHealth({ withoutLoading: true }) as Promise<any>,
			SystemService.admin.getCronStatus({ withoutLoading: true }) as Promise<any>,
			SystemService.admin.getBackups({ withoutLoading: true }) as Promise<any>,
		]);

		if (!this.isMounted_) return;

		this.setState({
			health: healthRes.status === 'fulfilled' ? (healthRes.value || null) : null,
			healthError: healthRes.status === 'rejected',
			crons: cronsRes.status === 'fulfilled' ? (cronsRes.value || null) : null,
			cronsError: cronsRes.status === 'rejected',
			backups: backupsRes.status === 'fulfilled' ? (backupsRes.value || null) : null,
			backupsError: backupsRes.status === 'rejected',
			loading: false,
			lastUpdated: new Date(),
		});
	}

	manualRefresh = () => {
		if (this.refreshTimer) {
			clearInterval(this.refreshTimer);
		}
		this.loadAll(false);
		this.refreshTimer = setInterval(() => {
			this.loadAll(false);
		}, REFRESH_INTERVAL_MS);
	}

	// ---------- Renders auxiliares ----------

	renderOverallBanner() {
		const { health, healthError, loading } = this.state;

		if (loading && !health) {
			return (
				<div className="overall-banner skeleton">
					<div className="skeleton-line" style={ { width: '40%' } }></div>
				</div>
			);
		}

		if (healthError || !health) {
			return (
				<div className="overall-banner critical">
					<span className="overall-icon">✕</span>
					<div className="overall-text">
						<h3>No se pudo cargar el estado del sistema</h3>
						<p>Revisa la conectividad con el backend.</p>
					</div>
				</div>
			);
		}

		const cls = statusClass(health.overall_status);
		const map: Record<string, { icon: string; title: string; subtitle: string }> = {
			ok:       { icon: '✓', title: 'Todo en orden',       subtitle: 'Todos los checks reportan estado saludable.' },
			warning:  { icon: '!', title: 'Atención requerida',  subtitle: 'Uno o más checks reportan advertencias.' },
			critical: { icon: '✕', title: 'Acción inmediata',    subtitle: 'Uno o más checks están en estado crítico.' },
			unknown:  { icon: '?', title: 'Estado desconocido',  subtitle: 'El sistema no reportó un estado global válido.' },
		};
		const info = map[cls];

		return (
			<div className={ `overall-banner ${ cls }` }>
				<span className="overall-icon">{ info.icon }</span>
				<div className="overall-text">
					<h3>{ info.title }</h3>
					<p>{ info.subtitle }</p>
				</div>
				{
					health.checked_at && (
						<span className="overall-meta">Check: { moment(health.checked_at).format('DD/MM/YYYY HH:mm:ss') }</span>
					)
				}
			</div>
		);
	}

	renderHealthCards() {
		const { health, loading } = this.state;
		const checks = health?.checks || {};

		const db = checks.database || {};
		const mem = checks.memory || {};
		const disk = checks.disk || {};
		const cpu = checks.cpu || {};

		const memPct = Number(mem.percent_used) || 0;
		const diskPct = Number(disk.percent_used) || 0;
		const cpuLoad = Number(cpu.load_1m) || 0;
		const cpuCount = Number(cpu.cpu_count) || 1;
		const cpuRatio = cpuCount > 0 ? cpuLoad / cpuCount : 0;
		const cpuOverloaded = cpuLoad >= cpuCount && cpuCount > 0;

		return (
			<div className="dashboard-grid">
				{ /* DB */ }
				<div className={ `health-card ${ statusClass(db.status) }` }>
					<div className="health-card-header">
						<span className="health-card-title">Base de datos</span>
						<span className={ `health-badge ${ statusClass(db.status) }` }>
							{ db.status ? String(db.status).toUpperCase() : (loading ? '…' : '—') }
						</span>
					</div>
					<div className="health-card-body">
						<div className="stat-value">{ formatLatency(db.latency_ms) }</div>
						<div className="stat-label">Latencia de conexión</div>
						{
							db.error_message && (
								<div className="health-error">{ db.error_message }</div>
							)
						}
					</div>
				</div>

				{ /* Memoria */ }
				<div className={ `health-card ${ statusClass(mem.status) }` }>
					<div className="health-card-header">
						<span className="health-card-title">Memoria</span>
						<span className={ `health-badge ${ statusClass(mem.status) }` }>
							{ mem.status ? String(mem.status).toUpperCase() : (loading ? '…' : '—') }
						</span>
					</div>
					<div className="health-card-body">
						<div className="stat-value">{ memPct.toFixed(1) }%</div>
						<div className="stat-label">
							{ formatNumber(mem.used_mb) } / { formatNumber(mem.total_mb) } MB
						</div>
						<div className="progress-bar">
							<div
								className={ `progress-fill ${ statusClass(mem.status) }` }
								style={ { width: `${ Math.min(100, memPct) }%` } }
							/>
						</div>
						{
							Number(mem.swap_used_mb) > 0 && (
								<div className="health-meta">
									Swap: { formatNumber(mem.swap_used_mb) } / { formatNumber(mem.swap_total_mb) } MB
								</div>
							)
						}
					</div>
				</div>

				{ /* Disco */ }
				<div className={ `health-card ${ statusClass(disk.status) }` }>
					<div className="health-card-header">
						<span className="health-card-title">Disco</span>
						<span className={ `health-badge ${ statusClass(disk.status) }` }>
							{ disk.status ? String(disk.status).toUpperCase() : (loading ? '…' : '—') }
						</span>
					</div>
					<div className="health-card-body">
						<div className="stat-value">{ diskPct.toFixed(1) }%</div>
						<div className="stat-label">
							{ formatNumber(disk.used_gb) } / { formatNumber(disk.total_gb) } GB
						</div>
						<div className="progress-bar">
							<div
								className={ `progress-fill ${ statusClass(disk.status) }` }
								style={ { width: `${ Math.min(100, diskPct) }%` } }
							/>
						</div>
						{
							disk.mount && (
								<div className="health-meta">Mount: { disk.mount }</div>
							)
						}
					</div>
				</div>

				{ /* CPU */ }
				<div className={ `health-card ${ cpuOverloaded ? 'warning' : statusClass(cpu.status) }` }>
					<div className="health-card-header">
						<span className="health-card-title">CPU</span>
						<span className={ `health-badge ${ cpuOverloaded ? 'warning' : statusClass(cpu.status) }` }>
							{ cpu.status ? String(cpu.status).toUpperCase() : (loading ? '…' : '—') }
						</span>
					</div>
					<div className="health-card-body">
						<div className="stat-value">{ cpuLoad.toFixed(2) }</div>
						<div className="stat-label">Load 1m / { cpuCount } core{ cpuCount === 1 ? '' : 's' }</div>
						<div className="progress-bar">
							<div
								className={ `progress-fill ${ cpuOverloaded ? 'warning' : statusClass(cpu.status) }` }
								style={ { width: `${ Math.min(100, cpuRatio * 100) }%` } }
							/>
						</div>
						<div className="health-meta">
							5m: { Number(cpu.load_5m || 0).toFixed(2) } · 15m: { Number(cpu.load_15m || 0).toFixed(2) }
						</div>
					</div>
				</div>
			</div>
		);
	}

	renderProcessInfo() {
		const { health, loading } = this.state;
		const checks = health?.checks || {};
		const up = checks.uptime || {};

		const startedAt = up.started_at ? moment(up.started_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss') : '—';

		return (
			<div className="info-card">
				<div className="info-card-header">
					<h4>Información del proceso</h4>
				</div>
				<div className="info-card-body">
					{
						loading && !health ? (
							<div className="info-card-loading">Cargando información…</div>
						) : (
							<div className="info-grid">
								<div className="info-item">
									<span className="info-label">Node version</span>
									<span className="info-value">{ checks.node_version || '—' }</span>
								</div>
								<div className="info-item">
									<span className="info-label">Plataforma</span>
									<span className="info-value">{ checks.platform || '—' }</span>
								</div>
								<div className="info-item">
									<span className="info-label">Process uptime</span>
									<span className="info-value">{ formatUptime(up.process_seconds) }</span>
								</div>
								<div className="info-item">
									<span className="info-label">System uptime</span>
									<span className="info-value">{ formatUptime(up.system_seconds) }</span>
								</div>
								<div className="info-item">
									<span className="info-label">Started at</span>
									<span className="info-value">{ startedAt }</span>
								</div>
								{
									up.pm2_restarts != null && (
										<div className="info-item">
											<span className="info-label">PM2 restarts</span>
											<span className="info-value">{ formatNumber(up.pm2_restarts) }</span>
										</div>
									)
								}
							</div>
						)
					}
				</div>
			</div>
		);
	}

	renderCronsSummary() {
		const { crons, cronsError, loading } = this.state;

		if (cronsError) {
			return (
				<div className="info-card error">
					<div className="info-card-header">
						<h4>Crones</h4>
					</div>
					<div className="info-card-body">
						<p className="info-error">No se pudo cargar el estado de los crones.</p>
					</div>
				</div>
			);
		}

		if (loading && !crons) {
			return (
				<div className="info-card">
					<div className="info-card-header">
						<h4>Crones</h4>
					</div>
					<div className="info-card-body">
						<div className="info-card-loading">Cargando jobs…</div>
					</div>
				</div>
			);
		}

		const g = crons?.global_stats || {};
		const jobs = Array.isArray(crons?.jobs) ? (crons!.jobs as CronJob[]) : [];
		const failed = Number(g.failed_24h) || 0;

		return (
			<div className="info-card">
				<div className="info-card-header">
					<h4>Crones</h4>
					<span className="info-card-sub">Resumen últimas 24h</span>
				</div>
				<div className="info-card-body">
					<div className="crons-stats">
						<div className="crons-stat">
							<span className="stat-value">{ formatNumber(g.total_runs_24h) }</span>
							<span className="stat-label">Runs 24h</span>
						</div>
						<div className="crons-stat">
							<span className="stat-value ok">{ formatNumber(g.successful_24h) }</span>
							<span className="stat-label">Successful</span>
						</div>
						<div className="crons-stat">
							<span className={ `stat-value ${ failed > 0 ? 'critical' : '' }` }>
								{ formatNumber(failed) }
							</span>
							<span className="stat-label">Failed</span>
						</div>
						<div className="crons-stat">
							<span className="stat-value">{ formatNumber(g.active_jobs) }</span>
							<span className="stat-label">Activos</span>
						</div>
						<div className="crons-stat">
							<span className="stat-value muted">{ formatNumber(g.disabled_jobs) }</span>
							<span className="stat-label">Disabled</span>
						</div>
					</div>

					<div className="jobs-list">
						{
							jobs.length === 0 && (
								<p className="no-items">Sin jobs configurados.</p>
							)
						}
						{
							jobs.map((j, idx) => {
								let badgeCls: string = 'unknown';
								if (!j.enabled) {
									badgeCls = 'disabled';
								}
								else if (j.is_running_now) {
									badgeCls = 'warning';
								}
								else {
									const lastSt = statusClass(j.last_status || '');
									if (lastSt === 'ok' || lastSt === 'critical' || lastSt === 'warning') {
										badgeCls = lastSt;
									}
									else {
										badgeCls = 'unknown';
									}
								}

								let badgeLabel = '—';
								if (!j.enabled) badgeLabel = 'Disabled';
								else if (j.is_running_now) badgeLabel = 'Running';
								else if (j.last_status) badgeLabel = String(j.last_status).toUpperCase();

								return (
									<div className="job-item" key={ j.job_name || idx }>
										<div className="job-item-main">
											<span className={ `job-badge ${ badgeCls }` }>{ badgeLabel }</span>
											<div className="job-info">
												<span className="job-name">{ j.job_name || '—' }</span>
												{
													j.schedule_description && (
														<span className="job-schedule">{ j.schedule_description }</span>
													)
												}
											</div>
										</div>
										<div className="job-item-meta">
											<span className="job-last-run">{ humanizeAgo(j.last_run) }</span>
										</div>
									</div>
								);
							})
						}
					</div>
				</div>
			</div>
		);
	}

	renderLastBackup() {
		const { backups, backupsError, loading } = this.state;

		if (backupsError) {
			return (
				<div className="info-card error">
					<div className="info-card-header">
						<h4>Último backup</h4>
					</div>
					<div className="info-card-body">
						<p className="info-error">No se pudo cargar el listado de backups.</p>
					</div>
				</div>
			);
		}

		if (loading && !backups) {
			return (
				<div className="info-card">
					<div className="info-card-header">
						<h4>Último backup</h4>
					</div>
					<div className="info-card-body">
						<div className="info-card-loading">Cargando backups…</div>
					</div>
				</div>
			);
		}

		const list = Array.isArray(backups?.backups) ? (backups!.backups as BackupRow[]) : [];
		// El listado puede venir ordenado por fecha descendente, pero no lo
		// asumimos: buscamos el más reciente por created_at.
		let latest: BackupRow | null = null;
		for (const b of list) {
			if (!latest) { latest = b; continue; }
			const ta = b.created_at ? moment(b.created_at) : null;
			const tb = latest.created_at ? moment(latest.created_at) : null;
			if (ta && (!tb || ta.isAfter(tb))) {
				latest = b;
			}
		}

		return (
			<div className="info-card">
				<div className="info-card-header">
					<h4>Último backup</h4>
					<span className="info-card-sub">
						{ formatNumber(backups?.total_count) } backup{ Number(backups?.total_count) === 1 ? '' : 's' } · { Number(backups?.total_size_mb || 0).toFixed(1) } MB totales
					</span>
				</div>
				<div className="info-card-body">
					{
						latest ? (
							<div className="backup-summary">
								<div className="backup-row">
									<span className="info-label">Archivo</span>
									<span className="info-value ellipsis">{ latest.filename || '—' }</span>
								</div>
								<div className="backup-row">
									<span className="info-label">Tipo</span>
									<span className="info-value">
										<span className="backup-type-pill">{ (latest.type || '—').toUpperCase() }</span>
									</span>
								</div>
								<div className="backup-row">
									<span className="info-label">Tamaño</span>
									<span className="info-value">{ Number(latest.size_mb || 0).toFixed(2) } MB</span>
								</div>
								<div className="backup-row">
									<span className="info-label">Creado</span>
									<span className="info-value">
										{ humanizeAgo(latest.created_at) }
										{
											latest.created_at && (
												<span className="info-muted"> · { moment(latest.created_at).format('DD/MM/YYYY HH:mm') }</span>
											)
										}
									</span>
								</div>
								{
									latest.description && (
										<div className="backup-row">
											<span className="info-label">Descripción</span>
											<span className="info-value">{ latest.description }</span>
										</div>
									)
								}
							</div>
						) : (
							<p className="no-items">No hay backups registrados.</p>
						)
					}
				</div>
			</div>
		);
	}

	render() {
		const { lastUpdated, loading } = this.state;
		// `now` se referencia por effect-of-render: re-renderea cada segundo
		// para refrescar el "hace X seg".
		const lastUpdatedLabel = lastUpdated ? humanizeAgo(lastUpdated.toISOString()) : '—';

		return (
			<div className="system-page dashboard-page">
				<div className="dashboard-header">
					<div className="dashboard-header-text">
						<h1>Dashboard</h1>
						<p>Estado general del sistema</p>
					</div>
					<div className="dashboard-header-actions">
						<span className="refresh-badge">
							<span className="refresh-dot"></span>
							Última actualización: { lastUpdatedLabel }
						</span>
						<button
							className="dashboard-refresh-btn"
							onClick={ this.manualRefresh }
							disabled={ loading }>
							{ loading ? 'Cargando…' : 'Refrescar' }
						</button>
					</div>
				</div>

				{ this.renderOverallBanner() }

				{ this.renderHealthCards() }

				<div className="dashboard-row">
					{ this.renderProcessInfo() }
					{ this.renderLastBackup() }
				</div>

				<div className="dashboard-row">
					{ this.renderCronsSummary() }
				</div>
			</div>
		);
	}
}

export default Dashboard;
